import { useParams } from 'react-router';

import { ApiError, errorMessage } from '@/api/client';
import { useConfig } from '@/api/queries';
import { NotFoundPage } from '@/app/errors';
import { configPath, useScopeParam } from '@/app/scope';
import { Button, LinkButton } from '@/components/Button';
import { History } from '@/components/icons';
import { ErrorNotice, Loading } from '@/components/primitives';
import { groupOf } from '@/domain/health';
import type { Scope } from '@/domain/types';

import { ConfigForm } from './ConfigForm';
import { PageHeader } from './shared';

export function ConfigEditPage() {
  const scope = useScopeParam();
  const { capabilityId } = useParams();
  if (!scope || !capabilityId) return <NotFoundPage />;
  return <ConfigEdit scope={scope} capabilityId={capabilityId} />;
}

function ConfigEdit({ scope, capabilityId }: { scope: Scope; capabilityId: string }) {
  const query = useConfig(scope, capabilityId);

  if (query.isPending) return <Loading label="Loading configuration…" />;
  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 404) return <NotFoundPage />;
    return (
      <ErrorNotice
        title="The configuration could not be loaded"
        message={errorMessage(query.error)}
        action={
          <Button size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }
  const config = query.data;
  const group = groupOf(config.capability_id);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        breadcrumb={[
          { label: 'Configuration', to: configPath(scope) },
          { label: <code>{config.capability_id}</code> },
        ]}
        title={
          <>
            {group ? <span className="text-ink-3 font-normal">{group} · </span> : null}
            {config.display_name}
          </>
        }
        actions={
          <LinkButton
            to={configPath(scope, config.capability_id, 'history')}
            icon={<History size={15} />}
          >
            Version history
          </LinkButton>
        }
      />
      <ConfigForm key={`${scope}/${config.capability_id}`} scope={scope} config={config} />
    </div>
  );
}
